import { useEffect, useState, useRef } from "react"
import { Link } from "react-router-dom"
import { supabase } from "../../supabaseClient"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import { getActiveYear } from "../../utils/activeYear"
import { setGameWinner, getRoundName } from "../../utils/bracket"

interface Team {
  id: number
  name: string
  seed: number
  region: string
  region_position: string
  wins: number
  is_eliminated: boolean
}

interface Game {
  id: number
  round: number
  region: string | null
  region_position: string | null
  bracket_position: number
  team1_id: number | null
  team2_id: number | null
  winner_id: number | null
  team1_score: number | null
  team2_score: number | null
  is_complete: boolean
}

export default function BracketManager() {
  const [teams, setTeams] = useState<Team[]>([])
  const [games, setGames] = useState<Game[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedRound, setSelectedRound] = useState(1)
  const [activeYear, setActiveYearState] = useState<number | null>(null)
  const [updating, setUpdating] = useState<number | null>(null)
  
  // Ref for round selector buttons to auto-scroll
  const roundButtonRefs = useRef<Map<number, HTMLButtonElement>>(new Map())
  
  // Confirmation modal state
  const [pendingWinner, setPendingWinner] = useState<{game: Game, team: Team, loser: Team} | null>(null)

  useEffect(() => { 
    loadActiveYear() 
  }, [])

  async function loadActiveYear() {
    const year = await getActiveYear()
    setActiveYearState(year)
    loadData(year)
  }

  async function loadData(year: number) {
    setLoading(true)
    
    const [teamsResult, gamesResult] = await Promise.all([
      supabase
        .from('tournament_teams')
        .select('id, seed, region, region_position, wins, is_eliminated, school:team_schools(name)')
        .eq('year', year),
      supabase.from('games').select('*').eq('year', year).order('bracket_position')
    ])

    // Flatten school name from join
    const teamsData = (teamsResult.data || []).map((t: any) => ({
      id: t.id,
      name: t.school?.name || 'Unknown',
      seed: t.seed,
      region: t.region,
      region_position: t.region_position,
      wins: t.wins || 0,
      is_eliminated: t.is_eliminated || false
    }))

    setTeams(teamsData)
    setGames(gamesResult.data || [])
    
    // Auto-select the current round:
    // 1. First check for an "in progress" round (some complete, some not)
    // 2. Otherwise find the first round with playable but unplayed games
    // 3. Default to the highest completed round or round 1
    const gamesData = gamesResult.data || []
    let currentRound = 1
    
    for (let r = 1; r <= 6; r++) {
      const roundGames = gamesData.filter((g: Game) => g.round === r)
      if (roundGames.length === 0) continue
      
      const completedCount = roundGames.filter((g: Game) => g.is_complete).length
      const playableCount = roundGames.filter((g: Game) => g.team1_id && g.team2_id).length
      const totalGames = roundGames.length
      
      // Round is "in progress" if some games are complete but not all playable games
      if (completedCount > 0 && completedCount < playableCount) {
        currentRound = r
        break
      }
      
      // Round is "upcoming" if no games complete but some are playable
      if (completedCount === 0 && playableCount > 0) {
        currentRound = r
        break
      }
      
      // If this round is fully complete, remember it and continue checking
      if (completedCount === totalGames && completedCount > 0) {
        currentRound = r
        // Keep going to see if next round has started
      }
    }
    
    setSelectedRound(currentRound)
    
    // Scroll the current round button into view after a short delay
    setTimeout(() => {
      const button = roundButtonRefs.current.get(currentRound)
      if (button) {
        button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
      }
    }, 100)
    
    setLoading(false)
  }

  function initiateSetWinner(game: Game, winnerId: number) {
    const winner = getTeam(winnerId)
    const loserId = game.team1_id === winnerId ? game.team2_id : game.team1_id
    const loser = getTeam(loserId)
    
    if (winner && loser) {
      setPendingWinner({ game, team: winner, loser })
    }
  }

  async function confirmSetWinner() {
    if (!pendingWinner) return
    
    const { game, team, loser } = pendingWinner
    if (!activeYear) return
    
    setUpdating(game.id)
    setPendingWinner(null)
    
    const success = await setGameWinner(
      game.id,
      team.id,
      loser.id,
      game.bracket_position,
      game.region_position,
      activeYear
    )

    if (success) {
      await loadData(activeYear)
    }
    
    setUpdating(null)
  }

  function getTeam(id: number | null): Team | undefined {
    return teams.find(t => t.id === id)
  }

  // Group games by round
  const gamesByRound = games.reduce((acc, game) => {
    if (!acc[game.round]) acc[game.round] = []
    acc[game.round].push(game)
    return acc
  }, {} as Record<number, Game[]>)

  const rounds = [1, 2, 3, 4, 5, 6]
  const currentRoundGames = gamesByRound[selectedRound] || []

  // Group current round games by region (for rounds 1-4)
  const gamesByRegion = currentRoundGames.reduce((acc, game) => {
    const key = game.region_position || 'finals'
    if (!acc[key]) acc[key] = []
    acc[key].push(game)
    return acc
  }, {} as Record<string, Game[]>)

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh] text-gray-500">Loading...</div>
  }

  if (games.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/admin" className="text-gray-400 hover:text-gray-600">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-[#1e3a5f]">Bracket Manager</h1>
            <p className="text-gray-500">Enter game results for <span className="font-semibold text-[#F15A22]">{activeYear}</span></p>
          </div>
        </div>

        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="py-8 text-center">
            <div className="text-amber-800 font-medium mb-2">No bracket games found</div>
            <p className="text-amber-600 text-sm mb-4">
              Go to Teams Setup and save all 64 teams to generate the bracket.
            </p>
            <Link to="/admin/years" className="text-[#1e3a5f] underline font-medium">
              → Go to Teams Setup
            </Link>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Confirmation Modal */}
      {pendingWinner && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="bg-white w-full max-w-sm overflow-hidden rounded-xl shadow-2xl">
            {/* Light Header with navy accent */}
            <div className="bg-white border-t-4 border-[#1e3a5f] p-4 text-center">
              <div className="text-lg font-bold text-gray-900">Confirm Winner</div>
            </div>
            <CardContent className="py-6">
              <div className="text-center">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <span className="w-8 h-8 bg-[#1e3a5f] text-white rounded-full flex items-center justify-center font-bold">
                      {pendingWinner.team.seed}
                    </span>
                    <span className="text-xl font-bold text-green-700">{pendingWinner.team.name}</span>
                  </div>
                  <div className="text-green-600 text-sm">defeats</div>
                  <div className="flex items-center justify-center gap-2 mt-1">
                    <span className="w-6 h-6 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center text-sm font-bold">
                      {pendingWinner.loser.seed}
                    </span>
                    <span className="text-gray-500 line-through">{pendingWinner.loser.name}</span>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  This will advance {pendingWinner.team.name} to the next round and eliminate {pendingWinner.loser.name}.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setPendingWinner(null)}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmSetWinner}
                    className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
                  >
                    Confirm Winner
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/admin" className="text-gray-400 hover:text-gray-600">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Bracket Manager</h1>
          <p className="text-gray-500">Enter game results for <span className="font-semibold text-[#F15A22]">{activeYear}</span></p>
        </div>
      </div>

      {/* Round Selector */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {rounds.map(round => {
          const roundGames = gamesByRound[round] || []
          const completedCount = roundGames.filter(g => g.is_complete).length
          const totalCount = roundGames.length
          const allComplete = totalCount > 0 && completedCount === totalCount
          
          return (
            <button
              key={round}
              ref={(el) => { if (el) roundButtonRefs.current.set(round, el) }}
              onClick={() => setSelectedRound(round)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                selectedRound === round
                  ? 'bg-[#1e3a5f] text-white'
                  : allComplete
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {getRoundName(round)}
              <span className="ml-2 opacity-70">
                {completedCount}/{totalCount}
              </span>
              {allComplete && selectedRound !== round && ' ✓'}
            </button>
          )
        })}
      </div>

      {/* Games Display */}
      {selectedRound <= 4 ? (
        // Rounds 1-4: Show by region
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {['top-left', 'top-right', 'bottom-left', 'bottom-right'].map(position => {
            const regionGames = gamesByRegion[position] || []
            const region = regionGames[0]?.region || position
            
            return (
              <Card key={position} className="bg-white border-0 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-[#1e3a5f]">
                    {region}
                    <span className="text-sm text-gray-400 font-normal ml-2">
                      ({position.replace('-', ' ')})
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {regionGames.length === 0 ? (
                    <div className="text-gray-400 text-sm">No games in this round</div>
                  ) : (
                    regionGames.map(game => (
                      <GameCard
                        key={game.id}
                        game={game}
                        team1={getTeam(game.team1_id)}
                        team2={getTeam(game.team2_id)}
                        onSelectWinner={(winnerId) => initiateSetWinner(game, winnerId)}
                        updating={updating === game.id}
                      />
                    ))
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      ) : (
        // Rounds 5-6: Final Four and Championship
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg text-[#1e3a5f]">{getRoundName(selectedRound)}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {currentRoundGames.length === 0 ? (
              <div className="text-gray-400 text-sm">
                Complete the Elite 8 to unlock Final Four matchups
              </div>
            ) : (
              currentRoundGames.map(game => (
                <GameCard
                  key={game.id}
                  game={game}
                  team1={getTeam(game.team1_id)}
                  team2={getTeam(game.team2_id)}
                  onSelectWinner={(winnerId) => initiateSetWinner(game, winnerId)}
                  updating={updating === game.id}
                  large
                />
              ))
            )}
          </CardContent>
        </Card>
      )}

      {/* Progress Summary */}
      <Card className="bg-gray-50 border-0">
        <CardContent className="py-4">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Tournament Progress</span>
            <span className="font-medium text-[#1e3a5f]">
              {games.filter(g => g.is_complete).length} / {games.length} games complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
            <div 
              className="bg-green-500 h-2 rounded-full transition-all"
              style={{ width: `${(games.filter(g => g.is_complete).length / games.length) * 100}%` }}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// Game Card Component - Click to select winner with larger targets
function GameCard({ 
  game, 
  team1, 
  team2, 
  onSelectWinner,
  updating,
  large = false
}: { 
  game: Game
  team1?: Team
  team2?: Team
  onSelectWinner: (winnerId: number) => void
  updating: boolean
  large?: boolean
}) {
  const canPlay = team1 && team2 && !game.is_complete

  if (game.is_complete) {
    // Completed game - show result with clearer winner/loser distinction
    const winner = game.winner_id === team1?.id ? team1 : team2
    const loser = game.winner_id === team1?.id ? team2 : team1
    
    return (
      <div className={`rounded-lg bg-gray-50 border border-gray-200 ${large ? 'p-4' : 'p-3'}`}>
        {/* Winner row - prominent */}
        <div className="flex items-center gap-2 mb-2">
          <span className={`${large ? 'w-7 h-7 text-sm' : 'w-6 h-6 text-xs'} bg-green-600 text-white rounded-full flex items-center justify-center font-bold shrink-0`}>
            {winner?.seed}
          </span>
          <span className={`font-semibold text-[#1e3a5f] ${large ? 'text-base' : 'text-sm'}`}>{winner?.name}</span>
          <span className="text-green-600 text-xs font-medium ml-auto">WIN</span>
        </div>
        {/* Loser row - muted */}
        <div className="flex items-center gap-2 opacity-50">
          <span className={`${large ? 'w-7 h-7 text-sm' : 'w-6 h-6 text-xs'} bg-gray-400 text-white rounded-full flex items-center justify-center font-bold shrink-0`}>
            {loser?.seed}
          </span>
          <span className={`line-through text-gray-500 ${large ? 'text-base' : 'text-sm'}`}>{loser?.name}</span>
        </div>
      </div>
    )
  }

  if (!canPlay) {
    // Waiting for teams
    return (
      <div className={`rounded-lg bg-gray-50 border border-gray-100 ${large ? 'p-4' : 'p-3'}`}>
        <TeamRow team={team1} large={large} />
        <div className="h-1" />
        <TeamRow team={team2} large={large} />
      </div>
    )
  }

  // Playable game - click to select winner
  return (
    <div className={`rounded-lg border border-gray-200 bg-white overflow-hidden ${large ? '' : ''}`}>
      {updating ? (
        <div className={`${large ? 'p-4' : 'p-3'} text-center text-gray-500`}>
          Updating...
        </div>
      ) : (
        <>
          {/* Team 1 - Clickable */}
          <button
            onClick={() => onSelectWinner(team1!.id)}
            className={`w-full flex items-center gap-3 ${large ? 'p-4' : 'p-3'} hover:bg-green-50 transition-colors border-b border-gray-100 group`}
          >
            <span className={`${large ? 'w-8 h-8 text-sm' : 'w-6 h-6 text-xs'} bg-[#1e3a5f] text-white rounded-full flex items-center justify-center font-bold shrink-0`}>
              {team1!.seed}
            </span>
            <span className={`flex-1 text-left ${large ? 'text-base' : 'text-sm'} font-medium text-gray-800`}>
              {team1!.name}
            </span>
            <span className={`${large ? 'px-4 py-2' : 'px-3 py-1.5'} rounded-lg text-xs font-semibold bg-gray-100 text-gray-400 group-hover:bg-green-500 group-hover:text-white transition-colors`}>
              Select Winner
            </span>
          </button>

          {/* Team 2 - Clickable */}
          <button
            onClick={() => onSelectWinner(team2!.id)}
            className={`w-full flex items-center gap-3 ${large ? 'p-4' : 'p-3'} hover:bg-green-50 transition-colors group`}
          >
            <span className={`${large ? 'w-8 h-8 text-sm' : 'w-6 h-6 text-xs'} bg-[#1e3a5f] text-white rounded-full flex items-center justify-center font-bold shrink-0`}>
              {team2!.seed}
            </span>
            <span className={`flex-1 text-left ${large ? 'text-base' : 'text-sm'} font-medium text-gray-800`}>
              {team2!.name}
            </span>
            <span className={`${large ? 'px-4 py-2' : 'px-3 py-1.5'} rounded-lg text-xs font-semibold bg-gray-100 text-gray-400 group-hover:bg-green-500 group-hover:text-white transition-colors`}>
              Select Winner
            </span>
          </button>
        </>
      )}
    </div>
  )
}

// Team Row Component
function TeamRow({ 
  team, 
  isWinner = false, 
  isLoser = false,
  large = false 
}: { 
  team?: Team
  isWinner?: boolean
  isLoser?: boolean
  large?: boolean
}) {
  if (!team) {
    return <div className="text-gray-400 text-sm italic py-1">TBD</div>
  }

  return (
    <div className={`flex items-center gap-2 ${isLoser ? 'opacity-50' : ''}`}>
      <span className={`${large ? 'w-7 h-7 text-sm' : 'w-5 h-5 text-xs'} ${isWinner ? 'bg-green-600' : 'bg-[#1e3a5f]'} text-white rounded-full flex items-center justify-center font-bold shrink-0`}>
        {team.seed}
      </span>
      <span className={`${large ? 'text-base' : 'text-sm'} ${isWinner ? 'font-bold text-green-700' : ''} ${isLoser ? 'line-through text-gray-400' : ''}`}>
        {team.name}
      </span>
      {isWinner && (
        <span className="text-green-500 text-xs font-bold ml-auto">WINNER</span>
      )}
    </div>
  )
}
